import type { CampaignAdProduct } from '@/types/spCampaigns';
import type { KeywordAdProduct } from '@/api/unifiedKeywords';

export const REPORT_AD_PRODUCT_OPTIONS = [
  { id: 'sponsored_products', label: 'Sponsored Products' },
  { id: 'sponsored_brands', label: 'Sponsored Brands' },
  { id: 'sponsored_display', label: 'Sponsored Display' },
] as const;

/**
 * Report API `ad_product` value for a catalog or report row.
 * Accepts short form (`SB`) and long form (`sponsored_brands`).
 */
export function campaignAdProductToReportId(adProduct: CampaignAdProduct | string): string {
  const raw = String(adProduct || '').trim();
  const upper = raw.toUpperCase();
  if (upper === 'SB' || raw === 'sponsored_brands' || upper.includes('BRAND')) {
    return 'sponsored_brands';
  }
  if (upper === 'SD' || raw === 'sponsored_display' || upper.includes('DISPLAY')) {
    return 'sponsored_display';
  }
  return 'sponsored_products';
}

/** Lookup key matching backend `get_profile_rows` aggregation (`ad_product::campaign_id`). */
export function campaignReportLookupKey(adProduct: CampaignAdProduct | string, campaignId: string): string {
  return `${campaignAdProductToReportId(adProduct)}::${campaignId}`;
}

/** Report API `ad_product` value for a unified keyword row source. */
export function keywordAdProductToReportId(adProduct: KeywordAdProduct | string): string {
  return campaignAdProductToReportId(adProduct);
}

/** Lookup key matching backend `get_keyword_profile_rows` (`ad_product::keyword_id`). */
export function keywordReportLookupKey(adProduct: KeywordAdProduct | string, keywordId: string): string {
  return `${keywordAdProductToReportId(adProduct)}::${keywordId}`;
}

export const KEYWORD_REPORT_AD_PRODUCT_IDS = ['sponsored_products', 'sponsored_brands'] as const;

export function keywordReportAdProductsQueryParam(): string {
  return reportAdProductsQueryParam([...KEYWORD_REPORT_AD_PRODUCT_IDS]);
}

export const TARGET_REPORT_AD_PRODUCT_IDS = [
  'sponsored_products',
  'sponsored_brands',
  'sponsored_display',
] as const;

export function targetReportAdProductsQueryParam(): string {
  return reportAdProductsQueryParam([...TARGET_REPORT_AD_PRODUCT_IDS]);
}

/** Lookup key matching backend `get_target_profile_rows` (`ad_product::target_id`). */
export function targetReportLookupKey(adProduct: string, targetId: string): string {
  return `${adProduct}::${targetId}`;
}

export function reportAdProductsQueryParam(selected: string[]): string {
  const all = REPORT_AD_PRODUCT_OPTIONS.map((o) => o.id);
  const use = selected.length ? selected : all;
  return [...use].sort().join(',');
}

export function reportAdProductShortLabel(adProduct: string): string {
  if (adProduct === 'sponsored_products') return 'SP';
  if (adProduct === 'sponsored_brands') return 'SB';
  if (adProduct === 'sponsored_display') return 'SD';
  return adProduct;
}
